// Détermine si on peut modifier un module
import {
  type LearningModule,
  LearningModuleInput
} from '@/core/domain/learning/module/LearningModule.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import {
  type LearningCourse,
  LearningCourseInput
} from '@/core/domain/learning/course/LearningCourse.ts'
import {
  type LearningGrain,
  LearningGrainInput
} from '@/core/domain/learning/grain/LearningGrain.ts'
import { useAuthentication } from '@/presentation/composables/auth/useAuthentication.ts'

/**
 * Composable concernant les accès aux parcours.
 */
export function useLearningCourseAccessUtils() {
  const { isContentApprover } = useAuthentication()
  /**
   * Détermine si la modification du parcours est possible.
   * @param course
   */
  const canEditCourse = (course: LearningCourse | LearningCourseInput) => {
    if (course.status === LearningStatus.Values.DRAFT) return true
    if (course.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la publication du parcours est possible.
   * @param course
   */
  const canPublishCourse = (course: LearningCourse | LearningCourseInput) => {
    if (course.status === LearningStatus.Values.DRAFT) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la suppression du parcours est possible.
   * @param course
   */
  const canDeleteCourse = (course: LearningCourse | LearningCourseInput) => {
    if (course.status === LearningStatus.Values.DRAFT) return true
    if (course.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
  }

  return {
    canEditCourse,
    canPublishCourse,
    canDeleteCourse
  }
}

/**
 * Composable concernant les accès aux modules.
 */
export function useLearningModuleAccessUtils() {
  const { isContentApprover } = useAuthentication()
  /**
   * Détermine si la modification du module est possible.
   * @param module
   */
  const canEditModule = (module: LearningModule | LearningModuleInput) => {
    if (module.status === LearningStatus.Values.DRAFT) return true
    if (module.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la publication du module est possible.
   * @param module
   */
  const canPublishModule = (module: LearningModule | LearningModuleInput) => {
    if (module.status === LearningStatus.Values.DRAFT) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la suppression du module est possible.
   * @param module
   */
  const canDeleteModule = (module: LearningModule | LearningModuleInput) => {
    if (module.status === LearningStatus.Values.DRAFT) return true
    if (module.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
    return false
  }

  return {
    canEditModule,
    canPublishModule,
    canDeleteModule
  }
}

/**
 * Composable concernant les accès aux grains.
 */
export function useLearningGrainAccessUtils() {
  const { isContentApprover } = useAuthentication()
  /**
   * Détermine si la modification du grain est possible.
   * @param grain
   */
  const canEditGrain = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return true
    if (grain.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la publication du grain est possible.
   * @param grain
   */
  const canPublishGrain = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return isContentApprover.value
    return false
  }

  /**
   * Détermine si la suppression du grain est possible.
   * @param grain
   */
  const canDeleteGrain = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return true
    if (grain.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
    return false
  }
  return {
    canEditGrain,
    canPublishGrain,
    canDeleteGrain
  }
}

/**
 * Composable concernant les accès aux supports.
 */
export function useLearningSupportAccessUtils() {
  const { isContentApprover } = useAuthentication()

  /**
   * Détermine si on peut ajouter un support de formation.
   * @param grain
   */
  const canAddLearningSupport = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return true
    if (grain.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
  }

  /**
   * Détermine si on peut modifier un support de formation.
   * @param grain
   */
  const canEditLearningSupport = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return true
    if (grain.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
  }

  /**
   * Détermine si on peut supprimer un support de formation.
   * @param grain
   */
  const canDeleteLearningSupport = (grain: LearningGrain | LearningGrainInput) => {
    if (grain.status === LearningStatus.Values.DRAFT) return true
    if (grain.status === LearningStatus.Values.ACTIVE) return isContentApprover.value
  }
  return {
    canAddLearningSupport,
    canEditLearningSupport,
    canDeleteLearningSupport
  }
}






